import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import {
  Callout,
  CurrencyNote,
  FaqList,
  H2,
  H3,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "Medicare, TRICARE & FEHB GLP-1 Coverage | Virginia Bariatric";
const DESCRIPTION =
  "The $50 Medicare GLP-1 Bridge, TRICARE's step-therapy and prescriber rules, and what FEHB plans must cover for Wegovy and Zepbound in Virginia.";
const URL = `${SITE}/medical-weight-loss/insurance/medicare-tricare-fehb`;
const NAME = "Medicare, TRICARE, and FEHB Weight-Loss Medication Coverage";

const REVIEWED = "October 25, 2026";
const REVIEWED_ISO = "2026-10-25";

const FAQS: Faq[] = [
  {
    q: "Does Medicare cover Wegovy or Zepbound?",
    a: "Yes, by one of two routes in 2027 — and your diagnosis decides which, not your preference.\n\nStarting January 1, 2027, Part D plans must carry Wegovy, because it becomes a Medicare-negotiated drug. Part D covers it to reduce cardiovascular risk in adults with established cardiovascular disease who also have overweight or obesity. Your plan sets your copay, which is not the same as the negotiated price. If you have type 2 diabetes, moderate to severe obstructive sleep apnea, or noncirrhotic MASH with moderate to advanced fibrosis, you are also routed to your regular Part D plan rather than the Bridge, though the covered drug and indication may differ.\n\nIf you have none of those conditions, your route is the Medicare GLP-1 Bridge — a CMS demonstration running through December 31, 2027 that covers certain GLP-1s prescribed for weight reduction and maintenance at a $50 monthly copay. It covers Wegovy injection, Wegovy tablets, Wegovy HD injection, Foundayo tablets, and the Zepbound KwikPen; other Zepbound formulations are not included.\n\nQualifying for one route rules out the other, and some people qualify for neither.",
  },
  {
    q: "Does TRICARE require you to try another weight-loss drug first?",
    a: "Yes. TRICARE requires a 3-month trial of generic phentermine, benzphetamine, diethylpropion, or phendimetrazine that failed to produce 5% weight loss, unless you have a contraindication, had an adverse reaction, or lost 5% but still need further reduction. TRICARE also requires at least 6 months of documented behavioral and dietary modification, and the prescriber must be an MTF or TRICARE network provider who has billed TRICARE for assessing you.",
  },
  {
    q: "Do federal employee (FEHB) plans cover weight-loss medication?",
    a: "For plan year 2026, OPM required every FEHB plan to cover at least one GLP-1 prescribed for weight loss plus at least two additional oral anti-obesity medications, so some coverage exists in every FEHB plan — though which drug is preferred varies and prior authorization applies. For plan year 2027, OPM has told carriers it will require intensive behavioral therapy before and during coverage.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/insurance/medicare-tricare-fehb")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/medicare-tricare-fehb") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({ url: URL, name: NAME, description: DESCRIPTION, lastReviewed: REVIEWED_ISO })
        ),
      },
    ] : [],
  }),
  component: GovernmentPlansPage,
});

function GovernmentPlansPage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Medicare, TRICARE, and Federal Employee Coverage
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          Government plans follow their own rules &mdash; including the new $50 Medicare GLP-1
          Bridge and TRICARE&rsquo;s step-therapy requirement.
        </p>
        <TelehealthCta />

      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote currentAsOf={REVIEWED} />
        <P>
          If you haven&rsquo;t read it yet, the{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            coverage overview
          </Link>{" "}
          explains the two gates and the diagnosis backdoor that these plans rely on heavily.
        </P>

        <H2 id="medicare">Medicare</H2>
        <P>
          There are two separate paths in 2027, and you do not get to choose between them. Your diagnosis decides which one applies — and qualifying for one rules out the other.
        </P>

        <H3>Path 1: Part D, if you have a qualifying condition</H3>
        <P>
          Starting January 1, 2027, Wegovy becomes a Medicare-negotiated drug with a maximum fair price of $274 per month, which means Part D plans must carry it. Ozempic is on the same list at the same price.
        </P>
        <P>
          Part D covers Wegovy to reduce the risk of heart attack, stroke, and cardiovascular death in adults with established cardiovascular disease who also have overweight or obesity. Your plan sets your actual copay, which is not the same as the $274 negotiated price. Prior authorization is likely and you will need a network pharmacy. Unlike the Bridge, this path has no end date.
        </P>
        <P>
          You are routed to Part D rather than the Bridge if you have moderate to severe obstructive sleep apnea, noncirrhotic MASH with moderate to advanced fibrosis, type 2 diabetes, or established cardiovascular disease where the medication is prescribed to reduce cardiovascular risk.
        </P>

        <H3>Path 2: the Medicare GLP-1 Bridge, if you do not</H3>
        <P>
          The Bridge is a CMS demonstration running July 1, 2026 through December 31, 2027. It covers GLP-1s prescribed for weight reduction and weight maintenance at a $50 monthly copay.
        </P>
        <P>
          Covered drugs are Wegovy injection, Wegovy tablets, Wegovy HD injection, Foundayo tablets, and the Zepbound KwikPen. Other formulations of Zepbound are not included — if you are on a Zepbound vial or autoinjector, this matters.
        </P>
        <P>
          You must be 18 or older and enrolled in a Part D plan. The $50 does not count toward your Part D deductible or your annual out-of-pocket cap, and low-income subsidy assistance does not apply to it.
        </P>
        <P>
          How it works in practice is unusual, and worth knowing in advance. Your pharmacy first submits a claim that is designed to be rejected. Only after that rejection can your prescriber submit the prior authorization — you cannot submit it yourself. The form records your BMI at the time you started the medication rather than your current BMI, along with whether you have prediabetes, a prior heart attack or stroke, symptomatic peripheral artery disease, heart failure with preserved ejection fraction, uncontrolled high blood pressure, or chronic kidney disease at stage 3a or higher.
        </P>
        <P>
          CMS has not published which combinations of BMI and condition actually qualify. Check your own eligibility at <a href="https://www.medicare.gov/glp1bridge" className="text-brand-deep underline hover:no-underline">Medicare.gov/glp1bridge</a> or by calling <a href="tel:18006334227" className="text-brand-deep underline hover:no-underline">1-800-MEDICARE</a>.
        </P>

        <Callout>
          <strong>Some people qualify for neither path.</strong>
          <P>
            If your BMI is between 27 and 30 and you have none of the listed conditions, or if your BMI is 35 or higher but you have no cardiovascular disease, you may fall between the two programs.
          </P>
          <P>
            If that is you, it is worth being properly evaluated. Undiagnosed sleep apnea, prediabetes, and fatty liver are common and frequently untested, and an accurate diagnosis can change which path is open to you. That is good medicine first and a coverage question second.
          </P>
        </Callout>

        <H3>Timing</H3>
        <P>
          Medicare Open Enrollment runs October 15 through December 7, 2026, with changes effective January 1, 2027. Read your plan's Annual Notice of Change to see how it is handling Wegovy, since plans differ in how they apply prior authorization to negotiated drugs.
        </P>

        <H2 id="tricare">TRICARE</H2>
        <P>
          TRICARE matters a great deal in Virginia given Hampton Roads and the Northern Virginia
          military community. Its criteria (current prior authorization form, dated July 15, 2026)
          include a step therapy requirement most commercial plans don&rsquo;t have. For adults
          seeking Wegovy or the Zepbound Pen Injector for weight management:
        </P>
        <UL>
          <li>
            BMI of 27 to 29 <strong>with</strong> at least one weight-related condition, or BMI of
            30 or higher
          </li>
          <li>
            Provider attestation of <strong>at least 6 months</strong> of behavioral modification
            and dietary restriction that failed, documented in the medical record
          </li>
          <li>
            A <strong>3-month trial</strong> of generic phentermine, benzphetamine, diethylpropion,
            or phendimetrazine that failed to produce 5% weight loss &mdash; unless you have a
            contraindication, had an adverse reaction, or lost 5% but still need further reduction
          </li>
          <li>
            Not pregnant, not using another GLP-1, and no personal or family history of medullary
            thyroid cancer or MEN type 2
          </li>
        </UL>
        <P>
          <strong>The prescriber must be an MTF or TRICARE network provider</strong> who has billed
          TRICARE for assessing you and developing a treatment plan. This is a hard stop &mdash; the
          request is denied without it.
        </P>
        <P>
          <strong>Ages 12 to 17:</strong> only Wegovy injection is available, requiring BMI at or
          above the 95th percentile. <strong>Approval runs 12 months</strong>, with annual renewal
          requiring at least 5% weight loss from baseline (4% for ages 12 to 17). For Zepbound and
          sleep apnea: adults with a documented apnea-hypopnea index of 15 or more events per hour
          and BMI of 30 or higher; Wegovy is not covered for this indication. Quantity limits are a
          30-day supply at retail, up to 60 days by mail order.
        </P>

        <H2 id="fehb">Federal employees (FEHB and PSHB)</H2>
        <P>
          Open Season for the 2027 plan year runs November 9 through December 14, 2026.
        </P>
        <P>
          Every FEHB and PSHB carrier is required to cover at least one GLP-1 anti-obesity medication and at least two oral anti-obesity medications. Which are preferred varies by plan, and prior authorization applies.
        </P>

        <H3>What changes in 2027: the therapy requirement</H3>
        <P>
          Beginning with the 2027 plan year, you must document participation in an evidence-based intensive behavioral therapy program both before and during treatment with an anti-obesity medication. This is new, it applies to every carrier, and it is not optional.
        </P>
        <P>
          Plans must cover 12 to 22 intensive behavioral therapy sessions per year, with the number depending on your progress, and must coordinate that therapy with their medication and metabolic surgery criteria. For children and adolescents aged six and older with a BMI above the 95th percentile, plans must cover qualifying programs of at least 26 contact hours over three to twelve months.
        </P>

        <H3>What to do about it</H3>
        <P>
          If you are already taking a GLP-1 through an FEHB plan, start the behavioral therapy documentation now rather than at your next renewal. The requirement applies before and during treatment, and a renewal submitted without it is a predictable denial — one that costs you weeks at a point when you are already mid-treatment.
        </P>
        <P>
          If you are starting fresh in 2027, build the therapy in from the beginning. Carriers are required to cover it, so the real cost is scheduling rather than money.
        </P>
        <P>
          Clinical criteria themselves have not changed much: most carriers use a BMI of 30, or 27 with a weight-related condition, though each sets its own.
        </P>

        <Callout>
          <strong>Worth comparing plans this year even if you are happy with yours.</strong>
          <P>
            FEHB enrollee premiums rise about 10.9% for 2027, and several FEHB plans and one PSHB plan are being discontinued — affected enrollees must choose a new plan or be moved to a default.
          </P>
          <P>
            Because carriers differ in which GLP-1 they prefer and how they administer the new therapy requirement, the plan that was best for you in 2026 may not be in 2027.
          </P>
        </Callout>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="more">Other coverage guides</H2>
        <SpokeCards exclude="/medical-weight-loss/insurance/medicare-tricare-fehb" />
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
